import axios, { AxiosError, AxiosRequestConfig, isAxiosError } from 'axios';
import path from 'path-browserify';
import { stringify as toQuery } from 'query-string';

// DEFAULTS
const DEFAULT_REQUEST_TIMEOUT = 15 * 1000;

let apiUrl;
let sessionToken;
let flashMessageHandler;
let globalErrorHandler: ((error: AxiosError) => void) | undefined;

export function parseApiError(error) {
    return error?.response?.data?.error ?? error;
}

export function isRetryableError(error): boolean {
    if (isAxiosError(error)) {
        return error.response ? error.response.status >= 500 : !axios.isCancel(error);
    }

    return error?.code >= 500;
}

function getRequestConfig(): AxiosRequestConfig {
    const config: AxiosRequestConfig = {
        timeout: DEFAULT_REQUEST_TIMEOUT,
    };

    if (sessionToken) {
        config.headers = { 'X-Session': sessionToken };
    }

    return config;
}

function handleResponse(response) {
    if (flashMessageHandler && response.data.messages) {
        flashMessageHandler(response.data.messages);
    }

    return response.data.data;
}

const globallyHandledErrors = new WeakSet<object>();

export const isGloballyHandledError = (value: unknown): boolean =>
    globallyHandledErrors.has(value as object);

function handleError(error: AxiosError): void {
    if (!globalErrorHandler) {
        throw error;
    }

    try {
        return globalErrorHandler(error);
    } catch (rethrown) {
        if (typeof rethrown === 'object' && rethrown !== null) {
            globallyHandledErrors.add(rethrown);
        }
        throw rethrown;
    }
}

export default {
    setApiUrl: (url) => (apiUrl = url),
    setFlashMessageHandler: (handler) => (flashMessageHandler = handler),
    setGlobalErrorHandler: (handler: (error: AxiosError) => void) => (globalErrorHandler = handler),
    setSessionToken: (token) => (sessionToken = token),

    get: (resource, params = {}, requestConfig?: AxiosRequestConfig) => {
        const query = toQuery(params);
        const url = apiUrl + path.join('/', resource) + (query ? '?' + query : '');
        return axios
            .get(url, { ...getRequestConfig(), ...requestConfig })
            .then(handleResponse)
            .catch(handleError);
    },
    patch: (resource, data, requestConfig?: AxiosRequestConfig) => {
        const url = apiUrl + path.join('/', resource);
        return axios
            .patch(url, data, { ...getRequestConfig(), ...requestConfig })
            .then(handleResponse)
            .catch(handleError);
    },
    post: (resource, data?, requestConfig?: AxiosRequestConfig) => {
        const url = apiUrl + path.join('/', resource);
        return axios
            .post(url, data, { ...getRequestConfig(), ...requestConfig })
            .then(handleResponse)
            .catch(handleError);
    },
    put: (resource, data, requestConfig?: AxiosRequestConfig) => {
        const url = apiUrl + path.join('/', resource);
        return axios
            .put(url, data, { ...getRequestConfig(), ...requestConfig })
            .then(handleResponse)
            .catch(handleError);
    },
    delete: (resource, params = {}, requestConfig?: AxiosRequestConfig) => {
        const query = toQuery(params);
        const url = apiUrl + path.join('/', resource) + (query ? '?' + query : '');
        return axios
            .delete(url, { ...getRequestConfig(), ...requestConfig })
            .then(handleResponse)
            .catch(handleError);
    },
};
